import { useEffect, useState } from "react";
import { CopyX, Loader2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, money } from "@/lib/api";

export const lierDoublon = async (paiement_id, remboursement_id, onChange) => {
  const { data } = await api.post("/doublons", { paiement_id, remboursement_id });
  const annuler = () => api.delete(`/doublons/${data.id}`).then(() => { toast.success("Lien retiré : les 2 opérations comptent de nouveau"); onChange(); }).catch((e) => toast.error(errMsg(e)));
  toast.success("Double paiement remboursé : les 2 opérations ne comptent plus dans les totaux", { action: { label: "Annuler", onClick: annuler }, duration: 10000 });
  return data;
};

const Candidats = ({ op, onClose, onChange, testId }) => {
  const [liste, setListe] = useState(null);
  const debit = op.debit > 0;
  useEffect(() => {
    api.get(`/doublons/candidats/${op.id}`).then((r) => setListe(r.data)).catch((e) => { toast.error(errMsg(e)); setListe([]); });
  }, [op.id]);
  const lier = async (c) => {
    try {
      await lierDoublon(debit ? op.id : c.id, debit ? c.id : op.id, onChange);
      onChange([op.id, c.id]);
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-w-xl" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle>{debit ? "Quel dépôt rembourse ce paiement en double ?" : "Quel paiement en double ce dépôt rembourse-t-il ?"}</DialogTitle>
          <DialogDescription>{op.date} · {op.description} · {money(op.debit || op.credit)}. Les 2 opérations restent visibles, mais ne comptent plus dans aucun total (retraits, dépôts, TPS/TVQ, tableau de bord, rapports).</DialogDescription>
        </DialogHeader>
        {liste === null ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          : liste.length === 0 ? <p className="text-sm text-slate-500" data-testid={`${testId}-aucun`}>Aucun {debit ? "dépôt" : "retrait"} du même montant dans l'année{debit ? " qui suit" : " qui précède"}.</p>
            : <div className="space-y-2">{liste.map((c) => (
              <button key={c.id} type="button" onClick={() => lier(c)} className="flex w-full items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 text-left text-sm transition-colors hover:border-emerald-500 hover:bg-emerald-50" data-testid={`${testId}-candidat-${c.id}`}>
                <span className="font-mono text-xs text-slate-500">{c.date}</span><span className="flex-1 truncate">{c.description}</span><span className="font-mono">{money(c.montant)}</span>
              </button>
            ))}</div>}
      </DialogContent>
    </Dialog>
  );
};

// Bouton « Doublon remboursé » d'une opération, ou pastille + « Délier » si elle est déjà liée
export const DoublonBouton = ({ op, onChange, testId }) => {
  const [ouvert, setOuvert] = useState(false);
  if (op.doublon) {
    const delier = () => api.delete(`/doublons/${op.doublon}`).then(() => { toast.success("Lien retiré : les 2 opérations comptent de nouveau"); onChange(); }).catch((e) => toast.error(errMsg(e)));
    return (
      <span className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-slate-100 px-2 py-1 text-xs text-slate-600" data-testid={`${testId}-lie`}>
        Double paiement remboursé · exclu des totaux
        <button type="button" onClick={delier} className="inline-flex items-center gap-1 text-emerald-700 hover:underline" data-testid={`${testId}-delier`}><Unlink className="h-3 w-3" />Délier</button>
      </span>
    );
  }
  return (
    <>
      <Button size="sm" variant="outline" className="h-9 bg-white" onClick={() => setOuvert(true)} data-testid={testId}
        title={op.debit > 0 ? "Paiement fait en double puis remboursé : le lier à son remboursement" : "Remboursement d'un paiement fait en double : le lier à ce paiement"}>
        <CopyX className="h-3.5 w-3.5 mr-1" />{op.debit > 0 ? "Doublon remboursé" : "Rembourse un doublon"}
      </Button>
      {ouvert && <Candidats op={op} onClose={() => setOuvert(false)} onChange={onChange} testId={testId} />}
    </>
  );
};
